import {get} from 'lodash';

import { forwardSpeedMax, forwardSpeedLimit, dvrUnknownSpeed, ONE_KM_IN_METERS, ONE_MILE_IN_KM, ONE_HOUR_TO_SECONDS, ONE_FEET_TO_METERS, forwardDistanceLimit } from './DvrConstants';
import { SYSTEM_MEASURE } from './SystemMeasure';

export const getVideoTime = media => {
  const capture = get(media, 'captures.0', {
    durationSeconds: 0,
    start: 0,
    end: 0
  });

  return {
    duration: parseInt(capture.durationSeconds),
    start: parseInt(capture.startDate),
    end: parseInt(capture.endDate)
  };
};

export const getOerTime = media => {
  const oerEvent = get(media, 'relatedItems.oerEvent');

  if (!oerEvent) {
    return false;
  }

  const firstSecond = get(media, 'relatedItems.oerEvent.mainData.perSecondData.0', {});

  const timeType = getOerTimeType(firstSecond);

  let eventTime = 0;

  if (timeType === 'gps') {
    eventTime = get(oerEvent, 'eventSecond.gpsTime', 0);
  } else {
    eventTime = get(oerEvent, 'eventSecond.rtcTime', 0);
  }

  const video = getVideoTime(media);
  const second = (eventTime - video.start) / 1000;

  return {
    timeType,
    start: eventTime,
    second,
    trigger: oerEvent.trigger
  };
};

export const getOerTimeType = secondData => {
  if (!secondData) {
    return 'rtc';
  }

  if (!secondData.gpsTime) {
    return 'rtc';
  }

  if (Math.abs(secondData.gpsTime - secondData.rtcTime) < 20000) {
    return 'gps';
  }

  return 'rtc';
};

export const getBrakeEvents = media => {
  return getMediaEvents(media, secondData => {
    return secondData.cruiseStatBrake === 'ON';
  });
};

export const getClutchEvents = media => {
  return getMediaEvents(media, secondData => {
    return secondData.cruiseStatClutch === 'ON';
  });
};

export const getCruiseEvents = media => {
  return getMediaEvents(media, secondData => {
    return secondData.cruiseStatActive === 'ACTIVE';
  });
};

export const getMediaEvents = (media, filterSecond) => {
  const video = getVideoTime(media);

  const perSecondData = get(media, 'relatedItems.oerEvent.mainData.perSecondData', []);
  const oerTimeType = getOerTimeType(perSecondData[0]);

  const events = [];

  perSecondData.forEach(secondData => {
    const secondTime = oerTimeType === 'gps' ? secondData.gpsTime : secondData.rtcTime;

    if (secondTime < video.start) {
      return;
    }

    if (secondTime >= video.end) {
      return;
    }

    if (filterSecond(secondData) === false) {
      return;
    }

    const second = (secondTime - video.start) / 1000;

    if (events.length > 0) {
      const previousEvent = events[events.length - 1];

      if (second === (previousEvent.endSecond + 1)) {
        previousEvent.endSecond = second;
        return;
      }
    }

    events.push({
      time: secondTime,
      startSecond: second,
      endSecond: second
    });
  });

  return events;
};

const secondDataToTimeframe = (
  secondData,
  useGpsTime,
  forwardCollisionEvent,
  accelerometerEvent,
  speedLimits,
  systemMeasure
) => {
  const DVR_UNKNOWN_SPEED = dvrUnknownSpeed(systemMeasure);
  const frame = {
    time: useGpsTime ? secondData.gpsTime : secondData.rtcTime,
    rpm: (secondData.rpm === 16383.75 ? null : secondData.rpm),
    gpsSpeed: (secondData.gpsSpeed === DVR_UNKNOWN_SPEED ? null : secondData.gpsSpeed),
    j1708Speed: (secondData.j1708Speed === DVR_UNKNOWN_SPEED ? null : secondData.j1708Speed),
    forwardSpeed: null,
    forwardSpeedPercent: null,
    forwardDistance: null,
    forwardDistancePercent: null,
    speedLimit: null,
    accelerationPedal: null,
    leftBlinkerOn: false,
    rightBlinkerOn: false,
    brakeOn: secondData.cruiseStatBrake === 'ON',
    clutchOn: secondData.cruiseStatClutch === 'ON',
    cruiseActive: secondData.cruiseStatActive === 'ACTIVE'
  };

  if (typeof(secondData.accelerationPedal) === 'number') {
    frame.accelerationPedal = parseFloat(secondData.accelerationPedal.toFixed(2));
  }

  const FORWARD_DISTANCE_LIMIT = forwardDistanceLimit(systemMeasure);
  const hasValidDistance = forwardCollisionEvent && forwardCollisionEvent.distance && forwardCollisionEvent.distance < FORWARD_DISTANCE_LIMIT;
  const hasValidSpeed = secondData.j1708Speed && secondData.j1708Speed != DVR_UNKNOWN_SPEED;

  if (hasValidDistance && hasValidSpeed) {
    const distanceInSeconds = getDistanceInSeconds(secondData.j1708Speed, forwardCollisionEvent.distance, systemMeasure);
    const FORWARD_COLLISION_EDGE_IN_SECONDS = 10;
    const distanceInSecPercent = ((parseFloat(distanceInSeconds) * 100) / FORWARD_COLLISION_EDGE_IN_SECONDS).toFixed(2);
    let percent = parseFloat(distanceInSecPercent) - 100;

    if (percent >= 0) {
      percent = 0;
    } else {
      percent = Math.abs(percent);
    }

    frame.forwardDistance = distanceInSeconds;
    frame.forwardDistancePercent = percent;
  }

  const FORWARD_SPEED_LIMIT = forwardSpeedLimit(systemMeasure);
  if (forwardCollisionEvent && forwardCollisionEvent.speed < FORWARD_SPEED_LIMIT) {
    const FORWARD_SPEED_MAX = forwardSpeedMax(systemMeasure);
    frame.forwardSpeed = forwardCollisionEvent.speed.toFixed(1);
    frame.forwardSpeedPercent = Math.abs((frame.forwardSpeed * 100) / FORWARD_SPEED_MAX);
  }

  if (accelerometerEvent) {
    frame.turnLightControl = accelerometerEvent.turnLightControl;
  }

  if (speedLimits) {
    const speedLimit = speedLimits.find(item => item.tCount === secondData.tCount);
    frame.speedLimit = get(speedLimit, 'maxSpeed', null);
  }

  return frame;
};

export const getTimelineData = (media, systemMeasure) => {
  const perSecondData = get(media, 'relatedItems.oerEvent.mainData.perSecondData', []);
  const forwardCollision = get(media, 'relatedItems.oerEvent.forwardCollisionData', []);
  const accelerationData = get(media, 'relatedItems.oerEvent.configAccelerationData', []);
  const speedLimits = get(media, 'relatedItems.speedLimit', []);
  const useGpsTime = getOerTimeType(perSecondData[0]) === 'gps';

  return perSecondData.map((secondData, index) => {
    const forwardCollisionEvent = get(forwardCollision, `forwardCollisionEvents.${index}`);
    const accelerometerEvent = get(accelerationData, `accelerometerEvents.${index}`);

    return secondDataToTimeframe(
      secondData,
      useGpsTime,
      forwardCollisionEvent,
      accelerometerEvent,
      speedLimits,
      systemMeasure
    );
  });
};

export const getDistanceInSeconds = (speed, distance, systemMeasure) => {
  return systemMeasure === SYSTEM_MEASURE.METRIC
    ? ((ONE_HOUR_TO_SECONDS * distance) / (speed * ONE_KM_IN_METERS)).toFixed(1)
    : ((ONE_HOUR_TO_SECONDS * distance * ONE_FEET_TO_METERS) / (speed * ONE_MILE_IN_KM * ONE_KM_IN_METERS)).toFixed(1);
};
